import React, { useEffect, useState, useRef } from 'react';
import { MdWarning, MdPerson, MdCalendarToday, MdRefresh, MdWorkOff, MdGroup, MdSearch, MdVisibility } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import UsibrasToast from '../../../components/UsibrasToast';
import UsibrasLoader from '../../../components/UsibrasLoader';
import { tokenManager } from '../../../utils/tokenManager';

const AbsentCheck = () => {
  const navigate = useNavigate();
  const [workerStatus, setWorkerStatus] = useState('all'); // all, current, new
  const [flaggedWorkers, setFlaggedWorkers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isBackgroundLoading, setIsBackgroundLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'info' });
  const [searchName, setSearchName] = useState('');
  const [cacheKey, setCacheKey] = useState('');
  const currentDataRef = useRef({ flagged_workers: [], summary: null });

  // Cache key based on worker status
  const getCacheKey = (status) => `absent_workers_cache_${status}`;

  // Load cached data from localStorage
  const loadCachedData = (status) => {
    try {
      const key = getCacheKey(status);
      const cached = localStorage.getItem(key);
      if (cached) {
        const parsedCache = JSON.parse(cached);
        // Check if cache is still valid (not older than 5 minutes)
        const cacheAge = Date.now() - (parsedCache.timestamp || 0);
        const fiveMinutes = 5 * 60 * 1000;
        
        if (cacheAge < fiveMinutes) {
          const cachedWorkers = parsedCache.flagged_workers || [];
          const cachedSummary = parsedCache.summary || null;
          setFlaggedWorkers(cachedWorkers);
          setSummary(cachedSummary);
          // Update ref
          currentDataRef.current = {
            flagged_workers: cachedWorkers,
            summary: cachedSummary,
          };
          return true;
        }
      }
    } catch (error) {
      console.error('Error loading cached data:', error);
    }
    return false;
  };

  // Save data to cache
  const saveToCache = (status, workers, summaryData) => {
    try {
      const key = getCacheKey(status);
      const cacheData = {
        flagged_workers: workers,
        summary: summaryData,
        timestamp: Date.now(),
      };
      localStorage.setItem(key, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error saving to cache:', error);
    }
  };

  // Check if data has changed
  const hasDataChanged = (oldData, newData) => {
    if (!oldData || !newData) return true;
    const oldIds = (oldData.flagged_workers || []).map(w => w.id).sort().join(',');
    const newIds = (newData.flagged_workers || []).map(w => w.id).sort().join(',');
    return oldIds !== newIds || 
           oldData.summary?.flagged_workers_count !== newData.summary?.flagged_workers_count;
  };

  const fetchAbsentWorkers = async (showLoader = true, isBackground = false) => {
    if (showLoader) {
      setIsLoading(true);
    } else {
      setIsBackgroundLoading(true);
    }
    
    try {
      const authHeader = tokenManager.getAuthHeader();
      if (!authHeader) {
        throw new Error('Authentication token not found. Please login again.');
      }

      const url = `https://usibras-web-backend.onrender.com/api/absent-consecutive-check/all?worker_status=${workerStatus}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || result.error || 'Failed to load absent workers');
      }

      const newData = {
        flagged_workers: result.flagged_workers || [],
        summary: result.summary || null,
      };

      // Check if data has changed using ref to avoid stale closure
      const hasChanged = hasDataChanged(currentDataRef.current, newData);

      // Update state
      setFlaggedWorkers(newData.flagged_workers);
      setSummary(newData.summary);
      
      // Update ref with new data
      currentDataRef.current = newData;

      // Save to cache
      saveToCache(workerStatus, newData.flagged_workers, newData.summary);

      // Show toast if data changed in background
      if (isBackground && hasChanged) {
        showToast('Data updated with latest information', 'success');
      }
    } catch (error) {
      if (!isBackground) {
        showToast(error.message || 'Failed to fetch absent workers data', 'error');
      }
      console.error('Error:', error);
      // Don't clear data on error, keep showing cached data
    } finally {
      if (showLoader) {
        setIsLoading(false);
      } else {
        setIsBackgroundLoading(false);
      }
    }
  };

  // Keep ref in sync with state
  useEffect(() => {
    currentDataRef.current = {
      flagged_workers: flaggedWorkers,
      summary: summary,
    };
  }, [flaggedWorkers, summary]);

  useEffect(() => {
    const key = getCacheKey(workerStatus);
    setCacheKey(key);
    
    // Try to load cached data first
    const hasCachedData = loadCachedData(workerStatus);
    
    if (hasCachedData) {
      // Show cached data immediately, then fetch fresh data in background
      // Use setTimeout to ensure cached data is displayed first
      setTimeout(() => {
        fetchAbsentWorkers(false, true);
      }, 100);
    } else {
      // No cache, fetch with loader
      fetchAbsentWorkers(true, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workerStatus]);

  const showToast = (message, type = 'info') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'info' }), 3000);
  };

  const handleRefresh = () => {
    // Clear cache and fetch fresh data
    try {
      localStorage.removeItem(cacheKey);
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
    fetchAbsentWorkers(true, false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (error) {
      return dateString;
    }
  };

  const calculateDaysSinceLastWork = (lastWorkDate) => {
    if (!lastWorkDate) return 'N/A';
    try {
      const lastDate = new Date(lastWorkDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      lastDate.setHours(0, 0, 0, 0);
      const diffTime = Math.abs(today - lastDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch (error) {
      return 'N/A';
    }
  };

  // Filter workers by search name
  const filteredWorkers = flaggedWorkers.filter(worker => {
    const workerName = worker.name || '';
    return workerName.toLowerCase().includes(searchName.toLowerCase());
  });

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <h1 className="text-3xl font-bold text-gray-800 flex items-center">
              <MdWorkOff className="mr-3 text-red-600" />
              Absent Workers Check
            </h1>
            {isBackgroundLoading && (
              <div className="flex items-center text-sm text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg">
                <MdRefresh className="mr-2 animate-spin" />
                <span>Updating...</span>
              </div>
            )}
          </div>
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center text-sm disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <MdRefresh className={`mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            {isLoading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
        <p className="text-gray-600">
          Workers who have been absent for 3 consecutive days
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Worker Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MdGroup className="inline mr-2 text-purple-500" />
              Worker Status
            </label>
            <select
              value={workerStatus}
              onChange={(e) => setWorkerStatus(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium"
            >
              <option value="all">All Workers</option>
              <option value="current">Current Workers</option>
              <option value="new">New Workers</option>
            </select>
          </div>

          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MdSearch className="inline mr-2 text-blue-500" />
              Search by Name
            </label>
            <div className="relative">
              <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search worker name..."
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
              {searchName && (
                <button
                  onClick={() => setSearchName('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Workers Checked</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{summary.total_workers_checked || 0}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <MdPerson className="text-2xl text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Flagged Workers</p>
                <p className="text-3xl font-bold text-red-600 mt-2">{summary.flagged_workers_count || 0}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <MdWarning className="text-2xl text-red-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Workers</p>
                <p className="text-3xl font-bold text-green-600 mt-2">
                  {(summary.total_workers_checked || 0) - (summary.flagged_workers_count || 0)}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <MdPerson className="text-2xl text-green-600" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Workers Table */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-800">Flagged Workers</h2>
            <span className="text-sm text-gray-600">
              {filteredWorkers.length} worker{filteredWorkers.length !== 1 ? 's' : ''} found
            </span>
          </div>
        </div>

        {isLoading && flaggedWorkers.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <UsibrasLoader />
          </div>
        ) : filteredWorkers.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <MdWorkOff className="text-6xl text-gray-300 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-600 mb-2">
              {searchName ? 'No workers found' : 'No flagged workers'}
            </p>
            <p className="text-sm text-gray-500">
              {searchName 
                ? `No workers match "${searchName}"`
                : 'Great! No workers have been absent for 3 consecutive days.'}
            </p>
            {searchName && (
              <button
                onClick={() => setSearchName('')}
                className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Clear Search
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-red-500 to-red-600 text-white">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Worker</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Card Number</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Start Date</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Last Work Date</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Days Since Last Work</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredWorkers.map((worker) => {
                  const daysSince = calculateDaysSinceLastWork(worker.last_work_date);
                  return (
                    <tr key={worker.id} className="hover:bg-red-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                              <MdPerson className="text-red-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{worker.name || 'N/A'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {worker.card_number || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          worker.status === 'Current' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {worker.status || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(worker.start_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(worker.last_work_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                          typeof daysSince === 'number' && daysSince >= 3
                            ? 'bg-red-100 text-red-800'
                            : 'bg-orange-100 text-orange-800'
                        }`}>
                          {typeof daysSince === 'number' ? `${daysSince} day${daysSince !== 1 ? 's' : ''}` : daysSince}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => navigate(`/admin/manualpeeling/worker/${worker.id}`)}
                          className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center space-x-1"
                        >
                          <MdVisibility className="text-lg" />
                          <span>View</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Loading Overlay */}
      {isLoading && <UsibrasLoader />}

      {/* Toast Notifications */}
      {toast.show && (
        <UsibrasToast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: 'info' })}
        />
      )}
    </div>
  );
};

export default AbsentCheck;

